import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Car02Icon, 
  Cancel01Icon, 
  Calendar03Icon, 
  CallIcon, 
  Location01Icon,
  Message01Icon,
  ArrowUp01Icon
} from "hugeicons-react";

interface SpeedDialProps {
  onOpenBooking: () => void;
  isBookingOpen: boolean;
}

export default function SpeedDial({ onOpenBooking, isBookingOpen }: SpeedDialProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState(false);

  // Show only after scrolling a bit
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShow(true);
      } else {
        setShow(false);
        setIsOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const BUSINESS_NUMBER = "+218930303046";

  const actions = [
    {
      icon: <ArrowUp01Icon size={20} />,
      label: "العودة للأعلى",
      onClick: () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setIsOpen(false);
      },
      color: "bg-[#2A2A2A]",
      textColor: "text-white",
    },
    {
      icon: <Calendar03Icon size={20} />,
      label: "احجز موعدك",
      onClick: () => {
        onOpenBooking();
        setIsOpen(false);
      },
      color: "bg-primary",
      textColor: "text-white",
    },
    {
      icon: <Location01Icon size={20} />,
      label: "موقعنا",
      onClick: () => {
        const el = document.getElementById("location");
        if (el) el.scrollIntoView({ behavior: "smooth" });
        setIsOpen(false);
      },
      color: "bg-blue-600",
      textColor: "text-white",
    },
    {
      icon: <CallIcon size={20} />,
      label: "اتصل بنا",
      onClick: () => {
        window.open(`tel:${BUSINESS_NUMBER}`, "_self");
        setIsOpen(false);
      },
      color: "bg-emerald-600",
      textColor: "text-white",
    },
    {
      icon: <Message01Icon size={20} />,
      label: "واتساب",
      onClick: () => {
        const message = encodeURIComponent("السلام عليكم، أود الاستفسار عن خدمات M Four");
        window.open(`https://wa.me/${BUSINESS_NUMBER.replace('+', '')}?text=${message}`, "_blank");
        setIsOpen(false);
      },
      color: "bg-green-600",
      textColor: "text-white",
    },
  ];

  return (
    <div className="fixed bottom-20 right-4 z-[70] md:hidden w-14">
      <AnimatePresence>
        {show && !isBookingOpen && (
          <div className="flex flex-col items-center gap-4">
            {/* Action Buttons */}
            <AnimatePresence>
              {isOpen && (
                <div className="flex flex-col items-center gap-3 mb-2">
                  {actions.map((action, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, scale: 0.3, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ 
                        opacity: 0, 
                        scale: 0.3, 
                        y: 10,
                        transition: { duration: 0.2, delay: index * 0.05 } 
                      }}
                      transition={{ 
                        type: "spring",
                        stiffness: 450,
                        damping: 30,
                        delay: (actions.length - index) * 0.05 
                      }}
                      onClick={action.onClick}
                      className="relative flex items-center justify-center w-14 group"
                    >
                      {/* Icon - FIXED vertically above the main button */}
                      <motion.div 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className={`w-12 h-12 rounded-full ${action.color} ${action.textColor} flex items-center justify-center shadow-[0_8px_25px_rgba(0,0,0,0.3)] border border-white/10 ring-4 ring-black/5 z-10`}
                      >
                        {action.icon}
                      </motion.div>

                      {/* Text Label - Expanding to the LEFT */}
                      <motion.span 
                        initial={{ opacity: 0, x: 20, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 10, scale: 0.8 }}
                        className="absolute right-full mr-4 bg-[#0a0a0a]/95 backdrop-blur-xl text-white px-4 py-2 rounded-full text-[11px] font-black shadow-2xl border border-white/10 whitespace-nowrap tracking-wide"
                        dir="rtl"
                      >
                        {action.label}
                      </motion.span>
                    </motion.button>
                  ))}
                </div>
              )}
            </AnimatePresence>

            {/* Main Toggle Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.5)] relative overflow-hidden transition-colors duration-500 border border-white/10 z-20 ${
                isOpen ? "bg-black text-white" : "bg-primary text-white"
              }`}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ scale: 0, rotate: -90, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    exit={{ scale: 0, rotate: 90, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 450, damping: 25 }}
                  >
                    <Cancel01Icon size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="car"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ 
                      x: [0, -6, 0], 
                      opacity: 1 
                    }}
                    transition={{
                      x: { repeat: Infinity, duration: 2, ease: "easeInOut" },
                      opacity: { duration: 0.3 }
                    }}
                    className="flex flex-col items-center"
                  >
                    <Car02Icon size={28} />
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!isOpen && (
                <motion.div 
                    className="absolute inset-0 bg-white/20"
                    animate={{ scale: [1, 2], opacity: [0.6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                />
              )}
            </motion.button>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
